import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { useViewOnly } from '@/components/templates/app-shell'
import { patchJSON } from '@/lib/api'
import type { EventRecord } from '@/pages/event-types'
import { PreviousEditionField, previousEditionError } from './previous-edition-field'

export function EventPreviousEdition({ event }: { event: EventRecord }) {
  const readOnly = useViewOnly()
  const client = useQueryClient()
  const [value, setValue] = React.useState(event.previous_edition ?? null)
  const save = useMutation({
    mutationFn: () =>
      patchJSON<{ event: EventRecord }>(`/api/v1/events/${event.id}`, {
        event: { previous_edition_id: value?.id ?? null },
      }),
    onSuccess: (data) => {
      client.setQueryData(['event', event.id], data)
      client.invalidateQueries({ queryKey: ['events'] })
      client.invalidateQueries({ queryKey: ['registration-comparison', String(event.id)] })
    },
  })
  const error = previousEditionError(save.error) ?? save.error?.message
  return (
    <section
      className="divide-y divide-border rounded-md border border-border bg-card"
      aria-label="Previous edition"
    >
      <PreviousEditionField
        eventId={event.id}
        startsAt={event.starts_at_local}
        value={value}
        readOnly={readOnly}
        pending={save.isPending}
        error={error}
        onChange={(next) => {
          setValue(next)
          save.reset()
        }}
      />
      {!readOnly && (
        <div className="px-4 py-3 sm:pl-[164px]">
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save previous edition'}
          </Button>
          {save.isSuccess && (
            <p className="text-caption text-muted-foreground">Previous edition saved</p>
          )}
        </div>
      )}
    </section>
  )
}
